import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useT } from '../../../../../_metronic/i18n/Metronici18n'
import { showConfirmAlert } from '../../../../../_metronic/helpers/AlertHelper'
import { extrairErrosProtocolo } from '../core/errosProtocolo'
import { criarRascunho, criarRascunhoDoModelo, criarRascunhoModelo } from '../core/_requests'

export type EscopoRascunho = 'versao' | 'modelo' | 'modeloPlataforma'

export type CriarRascunhoParams = {
  escopo: EscopoRascunho
  /** Empresa destino (escopos `versao` e `modelo`). */
  idEmpresa?: number
  /** Versão de origem: protocolo da empresa ou versão do modelo da plataforma. */
  idOrigem?: number
  /** Versão do modelo publicado, citada na confirmação do escopo `modelo`. */
  versaoModelo?: number
}

/**
 * Fluxo de "criar rascunho" (T7a AC4): confirmação → criação → navegação para o rascunho.
 * Compartilhado pelo botão (`CriarRascunhoButton`) e pelo item do menu de ações da listagem.
 * O rascunho sempre nasce com formulário próprio — a confirmação diz isso, porque é o que evita
 * surpresa com o bloqueio do formulário vigente.
 */
export const useCriarRascunho = ({
  escopo,
  idEmpresa,
  idOrigem,
  versaoModelo,
}: CriarRascunhoParams) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useT()
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const textoConfirmacao =
    escopo === 'modelo'
      ? t(
          'protocolos_triagem.criar_rascunho_modelo_confirmacao',
          { versao: versaoModelo ?? '' },
          `Será criado um rascunho a partir da versão ${
            versaoModelo ?? ''
          } do modelo Lauduz, com uma cópia do formulário do modelo na sua empresa.`
        )
      : escopo === 'modeloPlataforma'
      ? 'Será criado um rascunho do modelo da plataforma a partir desta versão.'
      : t(
          'protocolos_triagem.criar_rascunho_confirmacao',
          undefined,
          'Será criada uma nova versão em rascunho, com uma cópia do formulário desta versão.'
        )

  /** Cria o rascunho e navega até ele. Devolve a mensagem de erro (ou `null` se deu certo). */
  const executar = async (): Promise<string | null> => {
    setEnviando(true)
    setErro(null)
    try {
      if (escopo === 'modeloPlataforma') {
        const versao = await criarRascunhoModelo(idOrigem)
        await queryClient.invalidateQueries(['protocolos-triagem'])
        navigate(`/apps/protocolos-triagem/modelo-plataforma/${versao.id}`)
        return null
      }
      const novo =
        escopo === 'modelo'
          ? await criarRascunhoDoModelo(idEmpresa as number)
          : await criarRascunho(idEmpresa as number, idOrigem as number)
      await queryClient.invalidateQueries(['protocolos-triagem'])
      navigate(`/apps/protocolos-triagem/${novo.id}?empresa=${idEmpresa}`)
      return null
    } catch (excecao) {
      const mensagem = extrairErrosProtocolo(excecao).message
      setErro(mensagem)
      return mensagem
    } finally {
      setEnviando(false)
    }
  }

  /** Pede confirmação e, confirmado, cria. Devolve a mensagem de erro, se houver. */
  const confirmarECriar = async (): Promise<string | null> => {
    const confirmado = await showConfirmAlert({
      title: 'Criar rascunho?',
      text: textoConfirmacao,
      icon: 'question',
      confirmButtonText: 'Confirmar',
    })
    return confirmado ? executar() : null
  }

  return { confirmarECriar, enviando, erro }
}
